import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { AlertTriangle } from 'lucide-react';
import { POExtractionPanel } from './POExtractionPanel';
import { POLineItemsEditor } from './POLineItemsEditor';
import { DatePicker } from '../shared/DatePicker';

export const AddExtensionDialog = ({ open, onOpenChange, parentPo, form, setForm, onSubmit, onExtracted }) => {
  const parentMismatch = form.detected_parent_po_number && parentPo?.po_number
    && form.detected_parent_po_number.toUpperCase() !== parentPo.po_number.toUpperCase();
  return (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-extension-dialog">
      <DialogHeader>
        <DialogTitle>Add Extension PO</DialogTitle>
        <DialogDescription>
          Top up <span className="font-medium">{parentPo?.po_number}</span>'s total value. Sessions
          will keep billing against the parent PO number — the extension becomes its own individually
          searchable PO record (with its own line items) tagged "Extension" and linked back to the parent.
        </DialogDescription>
      </DialogHeader>
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label>Parent PO</Label>
          <Input value={parentPo?.po_number || ''} disabled data-testid="extension-parent-po-display" />
          {parentMismatch && (
            <p className="text-xs text-amber-600 flex items-center gap-1" data-testid="extension-parent-mismatch-warning">
              <AlertTriangle className="w-3 h-3" /> AI detected parent PO "{form.detected_parent_po_number}" on the uploaded document — please confirm this matches {parentPo?.po_number}.
            </p>
          )}
        </div>
        <POExtractionPanel poType="extension" onExtracted={onExtracted} initialFilename={form.document_filename} />
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="ext_po_number">Extension PO Number *</Label>
            <Input
              id="ext_po_number"
              value={form.po_number}
              onChange={(e) => setForm({ ...form, po_number: e.target.value.toUpperCase() })}
              placeholder="e.g. 101670794-EXT1"
              data-testid="extension-po-number-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ext_short_title">Short Title</Label>
            <Input
              id="ext_short_title"
              value={form.short_title || ''}
              onChange={(e) => setForm({ ...form, short_title: e.target.value })}
              placeholder="e.g. Q2 Top-up"
              data-testid="extension-short-title-input"
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="ext_amount">Extension Amount *</Label>
          <Input
            id="ext_amount"
            type="number"
            min="0"
            step="0.01"
            value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
            placeholder="25000.00"
            data-testid="extension-amount-input"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="ext_issue_date">Issue Date *</Label>
            <DatePicker
              value={form.issue_date}
              onChange={(v) => setForm({ ...form, issue_date: v })}
              placeholder="Select issue date"
              testId="extension-issue-date-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ext_expiry_date">Expiry Date</Label>
            <DatePicker
              value={form.expiry_date}
              onChange={(v) => setForm({ ...form, expiry_date: v })}
              placeholder="Select expiry date"
              testId="extension-expiry-date-input"
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="ext_sow">SOW Reference</Label>
          <Input
            id="ext_sow"
            value={form.sow_reference}
            onChange={(e) => setForm({ ...form, sow_reference: e.target.value })}
            placeholder="SOWFY27A004-EXT"
            data-testid="extension-sow-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="ext_ms_owner">Microsoft Owner</Label>
          <Input
            id="ext_ms_owner"
            value={form.ms_owner || ''}
            onChange={(e) => setForm({ ...form, ms_owner: e.target.value })}
            placeholder="e.g. Jane Doe (Microsoft)"
            data-testid="extension-ms-owner-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="ext_description">Description</Label>
          <Textarea
            id="ext_description"
            value={form.description || ''}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Description of extension scope..."
            rows={2}
            data-testid="extension-description-input"
          />
        </div>
        <POLineItemsEditor
          lineItems={form.line_items || []}
          onChange={(items) => setForm({ ...form, line_items: items })}
        />
        <div className="flex justify-end gap-3 pt-4 sticky bottom-0 bg-background border-t -mx-6 px-6 pb-1 mt-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="submit" className="bg-lime text-lime-foreground hover:bg-lime/90" data-testid="save-extension-button">
            Add Extension
          </Button>
        </div>
      </form>
    </DialogContent>
  </Dialog>
  );
};

export default AddExtensionDialog;
